import React, { useState, useEffect } from 'react'
import { useMutation } from 'react-query'
import { makeStyles } from '@material-ui/core/styles'
import {
    Input,
    Button,
    Picker,
    Loading,
    PageTitle
} from '../root/components/common'
import {
    Checkbox,
    FormControlLabel,
    Typography,
    FormControl
} from '@material-ui/core'
import { useSelector, useDispatch } from 'react-redux'
import * as actions from './actions'
import { withRouter } from 'react-router-dom'
import { enqueueSnackbar } from '../root/actions'
import { sendWelcomeEmails } from './api/customer-api'
import { useQuery } from 'react-query'
import { getRoleName } from 'web/src/utils'
import { Highlighted } from '../root/components/common/highlighted'
import { Tooltip } from '../root/components/common'
import { IconButton } from '@material-ui/core'
import { TableVirtualized } from '../root/components/common/table-virtualized/table'
import { mdiLogin } from '@mdi/js'
import Icon from '@mdi/react'
import { UserPermissions } from 'web/src/services/permissions'
import { getCustomerUsers } from './api/customer-api'

const useStyles = makeStyles(theme => ({
    wrapper: {
        height: 'calc(100% - 70px)'
    },
    tableWrapper: {
        height: 'calc(50% - 70px)'
        // width: 'calc(50% - 70px)'
    },
    detailsWrapper: {
        width: 500
    },
    input: {
        marginBottom: 15
    },
    pickerWrapper: {
        marginBottom: 15
    },
    checkboxWrapper: {
        marginTop: 15
    },
    buttonWrapper: {
        display: 'flex',
        alignItems: 'center'
    },
    button: {
        marginLeft: 10
    },
    formControl: {
        marginTop: 10,
        marginBottom: 10
    },
    textSecondary: {
        marginTop: '-5px'
    },
    searchInput: {
        width: '35%',
        marginTop: 10,
        marginBottom: 20
    },
    customerTableTitle: {
        color: theme.textSecondary,
        marginRight: 16,
        fontSize: 20,
        fontWeight: '100',
        fontFamily: theme.fontFamily
    }
}))

function Customer(props) {
    const classes = useStyles()
    const dispatch = useDispatch()
    const customerId = props.match.params.id

    const { customer, softwarePlans } = useSelector(state => state.customers)
    const { isLoading, locale } = useSelector(state => state.main)

    const [name, setName] = useState('')
    const [currentPlan, setCurrentPlan] = useState('')
    const [isHotSwapChecked, setHotSwapChecked] = useState(false)
    const [isUpdateServiceChecked, setUpdateServiceChecked] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')

    const handleHotSwapChange = event => {
        setHotSwapChecked(event.target.checked)
    }

    const handleUpdateServiceChange = event => {
        setUpdateServiceChecked(event.target.checked)
    }

    const permissions = {
        isSuperAdmin: UserPermissions.isSuperAdmin()
    }

    const {
        isLoading: isLoadingUsers,
        data = [],
        refetch
    } = useQuery(
        ['customer-users', customerId, searchTerm],
        () => getCustomerUsers(customerId, searchTerm),
        {
            refetchInterval: false,
            onError: () =>
                dispatch(
                    enqueueSnackbar({
                        message:
                            locale.customers.errorGettingUsersListForCustomer,
                        options: {
                            variant: 'error'
                        }
                    })
                )
        }
    )

    const sendWelcomeEmailMutation = useMutation(
        () => sendWelcomeEmails(customerId),
        {
            onSuccess: () => {
                dispatch(
                    enqueueSnackbar({
                        message: 'Welcome email sent successfully!',
                        options: {
                            variant: 'success'
                        }
                    })
                )
            },
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: 'Failed to send welcome email!',
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const handleSendWelcomeEmail = () => {
        sendWelcomeEmailMutation.mutate()
    }

    const isValidForSave = body => {
        let ok = true
        if (body.name.length < 3) {
            ok = false
            dispatch(
                enqueueSnackbar({
                    message: 'Name is too short',
                    options: {
                        variant: 'warning'
                    }
                })
            )
        }
        return ok
    }

    const save = (customerId, body) => {
        if (isValidForSave(body)) {
            dispatch(actions.updateCustomerRequest(customerId, body))
        }
    }

    useEffect(() => {
        refetch({ searchTerm })
    }, [searchTerm])

    useEffect(() => {
        dispatch(actions.getCustomerRequest(customerId))
        return () => {
            dispatch(actions.getCustomerSuccess(null))
        }
    }, [])

    useEffect(() => {
        setName(customer ? customer.name : '')
        setCurrentPlan(customer ? customer.software_plan_id : '')
        setHotSwapChecked(customer ? customer.allowed_hotswap_request : false)
        setUpdateServiceChecked(
            customer ? customer.allowed_update_service : false
        )
    }, [customer])

    const columns = [
        {
            key: 'name',
            label: locale.common.name,
            sortable: true,
            dataType: 'string',
            width: 1,
            render: ({ cellData }) => (
                <Highlighted text={cellData} highlight={searchTerm} />
            )
        },
        {
            key: 'role_code',
            label: locale.userManagement.roleName,
            sortable: false,
            width: 1,
            dataType: 'string',
            render: ({ rowData }) => {
                return getRoleName(
                    {
                        name: rowData.role_name,
                        code: rowData.role_code
                    },
                    locale
                )
            }
        },
        {
            key: 'actions',
            label: locale.common.login,
            sortable: false,
            dataType: 'custom',
            width: 0.5,
            alignCenter: true,
            render: ({ rowData }) => {
                return (
                    <Tooltip hover placement="top" text={locale.common.login}>
                        <IconButton
                            size="small"
                            onClick={() =>
                                dispatch(actions.impersonateUser(rowData.id))
                            }>
                            <Icon path={mdiLogin} size={1} />
                        </IconButton>
                    </Tooltip>
                )
            }
        }
    ]

    return (
        <div className={classes.wrapper}>
            {isLoading && <Loading />}
            {!isLoading && customer && (
                <React.Fragment>
                    <PageTitle title={customer.name} />
                    <div className={classes.detailsWrapper}>
                        <Input
                            className={classes.input}
                            label={locale.common.name}
                            type="text"
                            setValue={setName}
                            value={name}
                            onEnter={save.bind(this, customerId, {
                                name: name,
                                software_plan_id: Number(currentPlan)
                            })}
                        />
                        <div className={classes.pickerWrapper}>
                            <Picker
                                onChange={setCurrentPlan}
                                value={currentPlan}
                                options={softwarePlans}
                            />
                        </div>
                        <div className={classes.checkboxWrapper}>
                            <div className={classes.buttonWrapper}>
                                <FormControl
                                    component="fieldset"
                                    className={classes.formControl}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isHotSwapChecked}
                                                onChange={handleHotSwapChange}
                                            />
                                        }
                                        label={
                                            <div>
                                                <Typography variant="subtitle1">
                                                    {
                                                        locale.customers
                                                            .hotswapPortal
                                                    }
                                                </Typography>
                                                <Typography
                                                    className={
                                                        classes.textSecondary
                                                    }
                                                    variant="body2"
                                                    color="textSecondary">
                                                    {
                                                        locale.customers
                                                            .hotswapPortalAccess
                                                    }
                                                </Typography>
                                            </div>
                                        }
                                    />
                                </FormControl>
                                <Button
                                    reverted
                                    className={classes.button}
                                    onClick={handleSendWelcomeEmail}>
                                    {locale.customers.hotswapWelcomeEmail}
                                </Button>
                            </div>
                            <FormControl
                                component="fieldset"
                                className={classes.formControl}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isUpdateServiceChecked}
                                            onChange={handleUpdateServiceChange}
                                        />
                                    }
                                    label={
                                        <div className={classes.labelContainer}>
                                            <Typography variant="subtitle1">
                                                {
                                                    locale.customers
                                                        .hotswapUpdateService
                                                }
                                            </Typography>
                                            <Typography
                                                className={
                                                    classes.textSecondary
                                                }
                                                variant="body2"
                                                color="textSecondary">
                                                {
                                                    locale.customers
                                                        .hotswapUpdateServiceAccess
                                                }
                                            </Typography>
                                        </div>
                                    }
                                />
                            </FormControl>
                        </div>
                        <Button
                            onClick={save.bind(this, customerId, {
                                name: name,
                                software_plan_id: Number(currentPlan),
                                allowed_hotswap_request: isHotSwapChecked,
                                allowed_update_service: isUpdateServiceChecked
                            })}>
                            {locale.common.save}
                        </Button>
                    </div>
                    <hr style={{ marginBottom: '20px', marginTop: '15px' }} />
                    {permissions.isSuperAdmin && (
                        <div className={classes.tableWrapper}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                <p className={classes.customerTableTitle}>
                                    {locale.customers.customerUsersTable}
                                </p>
                                <Input
                                    className={classes.searchInput}
                                    label={locale.common.search}
                                    type="text"
                                    setValue={setSearchTerm}
                                    value={searchTerm}
                                    debounce={600}
                                />
                            </div>
                            <TableVirtualized
                                data={data}
                                rowCount={data.length}
                                isLoading={isLoadingUsers}
                                columns={columns}
                            />
                        </div>
                    )}
                </React.Fragment>
            )}
            {!isLoading && !customer && (
                <div>{locale.customers.noCustomer}</div>
            )}
        </div>
    )
}

export default withRouter(Customer)
